import { computed, type ComputedRef } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { fetchSubscriptionTypes, type SubscriptionType } from '@/api/subscriptionTypes'
import { subscriptionTypesPollingInterval } from '@/config/api'
import { useDrainPages } from '@/composables/useDrainPages'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

/**
 * Whole subscription catalog, shared with the Subscription pane through the
 * `['subscriptionTypes']` query key and drained so pickers and payment cards
 * can look a plan up by id. Only fetched when the viewer holds
 * `subscription_types_view`.
 */
export function useSubscriptionTypeCatalog(): {
  types: ComputedRef<SubscriptionType[]>
  canView: ComputedRef<boolean>
} {
  const { hasRole } = usePermissions()
  const canView = computed(() => hasRole(ResourceAccessRole.SubscriptionTypesView))

  const query = useInfiniteQuery({
    queryKey: ['subscriptionTypes'],
    queryFn: ({ pageParam }) => fetchSubscriptionTypes(pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    refetchInterval: subscriptionTypesPollingInterval,
    refetchIntervalInBackground: false,
    enabled: canView,
  })
  useDrainPages(query)

  const types = computed(() => (query.data.value?.pages ?? []).flatMap((p) => p.items ?? []))
  return { types, canView }
}
